// Atoms/Loader.tsx
import React from "react";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";

type LoaderVariant = "backdrop" | "inline";

type LoaderProps = {
  /** Show / hide the loader (especially for backdrop) */
  open?: boolean;
  /** "backdrop" = full-screen overlay, "inline" = simple row */
  variant?: LoaderVariant;
  /** CircularProgress color (MUI palette key or CSS color) */
  color?: "primary" | "secondary" | "inherit" | "success" | "error" | "info" | "warning";
  /** Optional message shown next to / under the spinner */
  text?: string;
  /** Color for the message text */
  textColor?: string;
  /** Size of spinner (px) */
  size?: number;
  /** Backdrop zIndex override */
  zIndex?: number;
  /** Optional blur effect for backdrop */
  blur?: boolean;
  /** Optional custom className */
  className?: string;
};

const Loader: React.FC<LoaderProps> = ({
  open = true,
  variant = "inline",
  color = "primary",
  text,
  textColor,
  size = 32,
  zIndex,
  blur = false,
  className,
}) => {
  if (variant === "backdrop") {
    return (
      <Backdrop
        open={open}
        className={className}
        sx={{
          color: "#fff",
          zIndex: (theme) => zIndex ?? theme.zIndex.modal + 1,
          backdropFilter: blur ? "blur(3px)" : "none",
          flexDirection: "column",
          opacity: 0,
        }}
      >
        <CircularProgress color={color} size={size} />
        {text ? (
          <Typography
            variant="body2"
            sx={{ color: textColor ?? "#fff", textAlign: "center" }}
          >
            {text}
          </Typography>
        ) : null}
      </Backdrop>
    );
  }

  // inline (simple) loader
  return open ? (
    <Box
      className={className}
      sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
    >
      <CircularProgress color={color} size={size} />
      {text ? (
        <Typography variant="body2" sx={{ color: textColor }}>
          {text}
        </Typography>
      ) : null}
    </Box>
  ) : null;
};

export default Loader;
